import { computed, makeObservable, observable, runInAction } from 'mobx';
import { AgenturStore } from '../agentur.store';
import { ISelectOpt } from 'app/components/common/Select';
import { formatBM } from 'app/utils';
import { AgenturModel } from 'app/models/agentur.model';
import _ from 'lodash';
import { BaseUiStore } from './base.ui.store';
import { AgtLinkedinStore, AgtLinkedinModel } from '../agt.linkedin.store';

export class LinkedinBm {
	bm: number;
	url: string;
	status: number;
	follower: number;
	views: number;
	reach: number;
	posts: number;

	constructor(bm: number, url: string, status: number, follower: number, views: number, reach: number, posts: number) {
		this.bm = bm;
		this.url = url ? (url.startsWith('http') ? url : `https://www.linkedin.com/in/${url}`) : url;
		this.status = status;
		this.follower = follower;
		this.views = views;
		this.reach = reach;
		this.posts = posts;
	}
}

export class AgtLinkedinVM {
	agt: AgenturModel;
	@observable
	linkedins: LinkedinBm[] = [];

	prevYearBm: number = 0;
	currYearBm: number = 0;
	currYear: number = 0;

	constructor(agt: AgenturModel, linkedins: AgtLinkedinModel[]) {
		makeObservable(this);
		this.agt = agt;
		const bms = [];
		for (const kv of linkedins) {
			const j = kv.j;
			const li = new LinkedinBm(kv.bm, j.u, j.s, j.f, j.v, j.r, j.p);
			bms.push(kv.bm);
			this.linkedins.push(li);
		}
		if (bms.length > 0) {
			this.currYearBm = _.max(bms)!;
			this.currYear = Math.round(this.currYearBm / 100);
			this.prevYearBm = (this.currYear - 1) * 100 + 12;
		}
	}

	@computed
	get prevLinkedin() {
		return this.linkedins.find((l) => l.bm === this.prevYearBm);
	}

	@computed
	get currLinkedin() {
		return this.linkedins.reduce<LinkedinBm | undefined>((max, li) => (li.bm > (max?.bm || 0) ? li : max), undefined);
	}

	@computed
	get prevFollower() {
		return this.prevLinkedin ? this.prevLinkedin.follower : 0;
	}

	@computed
	get currFollower() {
		return this.currLinkedin ? this.currLinkedin.follower : 0;
	}

	@computed
	get postsYtd() {
		return this.linkedins.reduce((acc, l) => {
			if (l.bm >= this.currYear * 100 && l.posts) {
				return acc + l.posts;
			}
			return acc;
		}, 0);
	}

	@computed
	get viewsYtd() {
		return this.linkedins.reduce((acc, l) => {
			if (l.bm >= this.currYear * 100 && l.views) {
				return acc + l.views;
			}
			return acc;
		}, 0);
	}

	@computed
	get reachYtd() {
		return this.linkedins.reduce((acc, l) => {
			if (l.bm >= this.currYear * 100 && l.reach) {
				return acc + l.reach;
			}
			return acc;
		}, 0);
	}
}

export class AgtLinkedinUiStore extends BaseUiStore<AgtLinkedinVM> {
	name = 'AgtLinkedinUiStore';
	agtLinkedinStore: AgtLinkedinStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, agtLinkedinStore: AgtLinkedinStore) {
		super();
		makeObservable(this);
		this.agtLinkedinStore = agtLinkedinStore;
		this.agenturStore = agenturStore;
	}

	@observable
	bms: number[] = [];

	@observable
	minBmCurrent?: number;

	@observable
	maxBmCurrent?: number;

	@computed
	get currentYear() {
		const mb = this.maxBm;
		if (mb === undefined) {
			return '';
		}
		return Math.floor(mb / 100);
	}

	@computed
	get maxBm() {
		if (this.maxBmCurrent) return this.maxBmCurrent;
		return _.max(this.bms);
	}

	@computed
	get minBm() {
		if (this.minBmCurrent) return this.minBmCurrent;
		return _.min(this.bms);
	}

	@computed
	get bmOpts(): ISelectOpt[] {
		return this.bms.map((bm) => {
			return {
				value: bm,
				label: formatBM(bm),
			};
		});
	}

	// range selected in the linkedin bericht (independent of minBmCurrent/maxBmCurrent)
	@observable
	rangeMinBm?: number;

	@observable
	rangeMaxBm?: number;

	@computed
	get ytdMaxBm() {
		return _.max(this.bms);
	}

	@computed
	get ytdMinBm() {
		const max = this.ytdMaxBm;
		if (!max) {
			return undefined;
		}
		const yearStart = Math.floor(max / 100) * 100 + 1;
		return _.min(this.bms.filter((bm) => bm >= yearStart));
	}

	@computed
	get selectedMinBm() {
		return this.rangeMinBm ?? this.ytdMinBm;
	}

	@computed
	get selectedMaxBm() {
		return this.rangeMaxBm ?? this.ytdMaxBm;
	}

	@observable
	linkedinBms: number[] = [];

	async _baseLoad() {
		const linkedinBms = await this.agtLinkedinStore.getDistinctBMs();
		this.linkedinBms = linkedinBms;

		runInAction(() => {
			this.bms = linkedinBms.sort();
		});
	}

	// loads all monthly rows of the given range and builds VMs from them, so the ytd getters
	// sum over the actual months. does NOT write this.items.
	async loadByBmRange(minBm: number, maxBm: number): Promise<AgtLinkedinVM[]> {
		const cacheKey = `AgtLinkedinUiStore.loadByBmRange${minBm}-${maxBm}`;

		return this.withCache(
			cacheKey,
			async () => {
				await this.load();

				const [linkedins, agts] = await Promise.all([this.agtLinkedinStore.loadByBmRange(minBm, maxBm), this.agenturStore.findAll()]);

				const linkedinsByAgtId = _.groupBy(linkedins, 'agtId');

				const res: AgtLinkedinVM[] = [];
				for (const agt of agts) {
					res.push(new AgtLinkedinVM(agt, linkedinsByAgtId[agt.agtId] || []));
				}
				return res;
			},
			{
				cacheResult: true,
				measureTime: true,
			},
		);
	}

	async loadBySelectedRange(): Promise<AgtLinkedinVM[]> {
		await this.load();
		const minBm = this.selectedMinBm;
		const maxBm = this.selectedMaxBm;
		if (!minBm || !maxBm) {
			return [];
		}
		return this.loadByBmRange(minBm, maxBm);
	}

	async loadByMaxBmForYear(yearBm: number) {
		const cacheKey = `AgtLinkedinUiStore.loadByBm${this.maxBm}-${this.minBm}`;

		return this.withCache(
			cacheKey,
			async () => {
				await this.load();

				const maxBmorEndofYear = (bm: number) => {
					if (bm > (yearBm + 1) * 100) return yearBm * 100 + 12;
					return bm;
				};

				const maxLinkedinBm = maxBmorEndofYear(_.max(this.linkedinBms)!);

				const [linkedins, agts] = await Promise.all([
					this.agtLinkedinStore.loadByBm(maxLinkedinBm),
					this.agenturStore.findAll()
				]);

				const res: AgtLinkedinVM[] = [];
				for (const agt of agts) {
					let li = linkedins.filter((d) => d.agtId === agt.agtId);
					const vm = new AgtLinkedinVM(agt, li);
					res.push(vm);
				}

				runInAction(() => {
					this.items = res;
				});

				return this.items;
			},
			{
				cacheResult: true,
				measureTime: true,
			}
		);
	}
}
